import React, { useEffect, useMemo, useState } from 'react';
import { serviceApi } from '../api/service';
import { userApi } from '../api/user';
import { ServiceResponse, MemberResponse, UserResponse } from '../types';
import {
  Plus,
  X,
  Trash2,
  Save,
  Globe,
  ChevronRight,
  ArrowLeft,
  Activity,
  PenSquare,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoadingScreen from '../components/LoadingScreen';
import { getServiceDisplayName, getServiceRoleLabel } from '../utils/roles';

type ServiceStats = Record<string, { memberCount: number }>;

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const SERVICE_TOPBAR_KEY = 'service-topbar-breadcrumb';

const setServiceTopbarBreadcrumb = (code: string | null, label: string | null) => {
  if (code && label) {
    localStorage.setItem(SERVICE_TOPBAR_KEY, JSON.stringify({ code, label }));
  } else {
    localStorage.removeItem(SERVICE_TOPBAR_KEY);
  }
  window.dispatchEvent(new Event('service-topbar-change'));
};

const ServicesPage: React.FC = () => {
  const { user } = useAuth();

  const [services, setServices] = useState<ServiceResponse[]>([]);
  const [serviceStats, setServiceStats] = useState<ServiceStats>({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');

  const [selectedService, setSelectedService] = useState<ServiceResponse | null>(null);
  const [members, setMembers] = useState<MemberResponse[]>([]);
  const [memberLoading, setMemberLoading] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  const [serviceForm, setServiceForm] = useState({ code: '', name: '', description: '' });
  const [serviceEditForm, setServiceEditForm] = useState({ name: '', description: '' });
  const [availableUsers, setAvailableUsers] = useState<UserResponse[]>([]);
  const [newMemberData, setNewMemberData] = useState({ userId: 0, role: 'SERVICE_VIEWER' });

  const serviceDisplayName = useMemo(
    () => (selectedService ? getServiceDisplayName(selectedService.code) : '서비스'),
    [selectedService]
  );

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await serviceApi.getServices();
      const nextServices = response.data.result || [];
      setServices(nextServices);

      const statsEntries = await Promise.all(
        nextServices.map(async (service) => {
          try {
            const membersRes = await serviceApi.getMembers(service.code);
            return [service.code, { memberCount: (membersRes.data.result || []).filter((member) => member.status === 'ACTIVE').length }] as const;
          } catch {
            return [service.code, { memberCount: 0 }] as const;
          }
        })
      );

      setServiceStats(Object.fromEntries(statsEntries));
    } catch (error) {
      console.error('Failed to load services', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceMembers = async (serviceCode: string) => {
    setMemberLoading(true);
    try {
      const response = await serviceApi.getMembers(serviceCode);
      setMembers((response.data.result || []).filter((member) => member.status === 'ACTIVE'));
    } catch (error) {
      console.error('Failed to load service members', error);
      setMembers([]);
    } finally {
      setMemberLoading(false);
    }
  };

  const openServiceDetail = async (service: ServiceResponse) => {
    setServiceTopbarBreadcrumb(service.code, service.code);
    setSelectedService(service);
    setViewMode('detail');
    setMembers([]);
    try {
      const response = await serviceApi.getService(service.code);
      const nextService = response.data.result || service;
      setSelectedService(nextService);
      setServiceEditForm({
        name: nextService.name,
        description: nextService.description || '',
      });
      await fetchServiceMembers(nextService.code);
    } catch (error) {
      console.error('Failed to load service detail', error);
      await fetchServiceMembers(service.code);
    }
  };

  const backToList = () => {
    setServiceTopbarBreadcrumb(null, null);
    setSelectedService(null);
    setMembers([]);
    setViewMode('list');
    setShowEditModal(false);
    setShowAddMemberModal(false);
    fetchServices();
  };

  const handleCreateService = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await serviceApi.createService(serviceForm);
      setShowCreateModal(false);
      setServiceForm({ code: '', name: '', description: '' });
      await fetchServices();
    } catch (error) {
      alert('서비스 생성에 실패했습니다.');
    }
  };

  const handleUpdateService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService) return;
    try {
      const response = await serviceApi.updateService(selectedService.code, {
        name: serviceEditForm.name,
        description: serviceEditForm.description,
      });
      if (response.data.result) {
        setSelectedService(response.data.result);
      }
      setShowEditModal(false);
      await fetchServices();
    } catch (error) {
      alert('서비스 수정에 실패했습니다.');
    }
  };

  const handleDeleteService = async () => {
    if (!selectedService) return;
    const confirmed = window.confirm(`서비스 "${selectedService.name}"를 삭제하시겠습니까?`);
    if (!confirmed) return;
    try {
      await serviceApi.deleteService(selectedService.code);
      backToList();
    } catch (error: any) {
      alert(error.response?.data?.message || '서비스 삭제에 실패했습니다.');
    }
  };

  const openAddMemberModal = async () => {
    if (!selectedService) return;
    setNewMemberData({
      userId: 0,
      role: 'SERVICE_VIEWER',
    });
    setShowAddMemberModal(true);
    try {
      const response = await userApi.getUsers();
      setAvailableUsers(response.data.result || []);
    } catch (error) {
      console.error('Failed to load users', error);
      setAvailableUsers([]);
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService || newMemberData.userId === 0) return;
    try {
      await serviceApi.addMember(selectedService.code, {
        userId: newMemberData.userId,
        role: newMemberData.role,
      });
      setShowAddMemberModal(false);
      await fetchServiceMembers(selectedService.code);
      await fetchServices();
    } catch (error) {
      alert('멤버 추가에 실패했습니다.');
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!selectedService) return;
    if (!window.confirm('정말 제거하시겠습니까?')) return;
    try {
      await serviceApi.removeMember(selectedService.code, userId);
      await fetchServiceMembers(selectedService.code);
      await fetchServices();
    } catch (error) {
      alert('멤버 제거에 실패했습니다.');
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (!selectedService) return;
    setServiceEditForm({
      name: selectedService.name,
      description: selectedService.description || '',
    });
  }, [selectedService]);

  if (viewMode === 'detail' && selectedService) {
    return (
      <div className="fade-in">
        <div className="service-detail-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.9rem' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <button onClick={backToList} className="btn btn-outline service-back-btn" style={{ padding: '0.5rem', borderRadius: '50%' }}>
              <ArrowLeft size={20} />
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <h1 className="service-detail-title" style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>{selectedService.name}</h1>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowEditModal(true)}
                  style={{ padding: '0.45rem 0.7rem' }}
                  aria-label="서비스 편집"
                >
                  <PenSquare size={16} />
                </button>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: '0.35rem' }}>
                {selectedService.description || '서비스 설명이 등록되지 않았습니다.'}
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.85rem' }}>
                <span className="badge badge-inactive" style={{ textTransform: 'none' }}>{selectedService.code}</span>
                <span className={`badge badge-${selectedService.status.toLowerCase()}`}>{selectedService.status}</span>
                <span className="badge badge-inactive" style={{ textTransform: 'none' }}>
                  멤버 {serviceStats[selectedService.code]?.memberCount ?? members.length}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={handleDeleteService} style={{ color: '#b91c1c', borderColor: '#fecaca' }}>
                <Trash2 size={16} /> 서비스 삭제
              </button>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gap: '1rem' }}>
          <div className="card shadow-sm service-members-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="service-members-head">
              <h3 className="service-subtitle">{serviceDisplayName} 유저</h3>
              <button className="btn btn-primary" style={{ padding: '0.5rem 1rem' }} onClick={openAddMemberModal}>
                <Plus size={16} /> 유저 추가
              </button>
            </div>

            {memberLoading ? (
              <div style={{ padding: '2rem' }}>
                <LoadingScreen inline title="서비스 유저를 불러오는 중" copy="멤버 목록을 확인하고 있습니다." />
              </div>
            ) : members.length > 0 ? (
              <div className="service-member-list">
                {members.map((member) => (
                  <div key={member.userId} className="service-member-row">
                    <div className="service-member-avatar">
                      {member.name.charAt(0)}
                    </div>

                    <div className="service-member-main">
                      <div className="service-member-name">{member.name}</div>
                      <div className="service-member-email">{member.email}</div>
                    </div>

                    <div className="service-member-meta">
                      <span className="badge badge-active" style={{ textTransform: 'none' }}>
                        ACTIVE
                      </span>
                      <span className="badge badge-inactive" style={{ textTransform: 'none' }}>
                        {getServiceRoleLabel(selectedService.code, member.role as any)}
                      </span>
                      <span className="service-member-date">{formatDate(member.createdAt)}</span>
                    </div>

                    <div className="service-member-actions">
                      <button className="btn btn-danger" style={{ padding: '0.4rem' }} onClick={() => handleRemoveMember(member.userId)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  ))}
              </div>
            ) : (
              <div style={{ padding: '1.5rem', color: 'var(--text-muted)' }}>
                아직 이 서비스에 연결된 유저가 없습니다.
              </div>
            )}
          </div>
        </div>

        {showAddMemberModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ padding: '2.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '800' }}>유저 추가</h2>
                <button onClick={() => setShowAddMemberModal(false)} className="btn btn-outline" style={{ border: 'none' }}>
                  <X />
                </button>
              </div>
              <form onSubmit={handleAddMember} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div style={{ padding: '1rem', background: '#f0f9ff', borderRadius: '0.75rem', fontSize: '0.875rem', color: '#0369a1' }}>
                  <strong>{selectedService.name}</strong>에 유저를 추가합니다.
                </div>
                <div>
                  <label className="label">유저 선택</label>
                  <select
                    className="input"
                    value={newMemberData.userId}
                    onChange={(e) => setNewMemberData({ ...newMemberData, userId: parseInt(e.target.value, 10) })}
                    required
                  >
                    <option value={0}>대상 사용자를 선택하세요</option>
                    {availableUsers.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name} ({option.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">부여할 역할</label>
                  <select
                    className="input"
                    value={newMemberData.role}
                    onChange={(e) => setNewMemberData({ ...newMemberData, role: e.target.value })}
                    required
                  >
                    <option value="SERVICE_VIEWER">유저 추가</option>
                  </select>
                </div>
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
                  <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowAddMemberModal(false)}>
                    취소
                  </button>
                  <button type="submit" className="btn btn-primary" style={{ flex: 2 }}>
                    추가하기
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ padding: '2.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '800' }}>서비스 정보 수정</h2>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: '0.35rem' }}>
                    이름과 설명만 수정할 수 있습니다.
                  </p>
                </div>
                <button onClick={() => setShowEditModal(false)} className="btn btn-outline" style={{ border: 'none' }}>
                  <X />
                </button>
              </div>
              <form onSubmit={handleUpdateService} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div>
                  <label className="label" style={{ marginBottom: '0.5rem' }}>서비스 이름</label>
                  <input
                    className="input"
                    value={serviceEditForm.name}
                    onChange={(e) => setServiceEditForm({ ...serviceEditForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label" style={{ marginBottom: '0.5rem' }}>서비스 설명</label>
                  <textarea
                    className="input"
                    rows={4}
                    value={serviceEditForm.description}
                    onChange={(e) => setServiceEditForm({ ...serviceEditForm, description: e.target.value })}
                    placeholder="서비스의 목적과 용도를 입력하세요."
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowEditModal(false)}>
                    취소
                  </button>
                  <button type="submit" className="btn btn-primary" style={{ flex: 2 }}>
                    <Save size={18} /> 저장
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
      <div className="fade-in">
        <div className="services-header">
          <div>
          <h1 style={{ fontSize: '1.45rem', fontWeight: 500 }}>서비스 관리</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem' }}>
            시스템에서 운영 중인 서비스 목록입니다.
          </p>
        </div>
        {user?.globalRole === 'ROOT_ADMIN' && (
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} /> 서비스 등록
          </button>
        )}
      </div>

      <div className="card shadow-sm services-list-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ background: '#f8fafc', padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: '700' }}>
            <Activity size={14} /> STATUS
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: '700', marginLeft: 'auto' }}>
            <Globe size={14} /> TOTAL: {services.length}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {loading && services.length === 0 ? (
            <div style={{ padding: '2rem' }}>
              <LoadingScreen inline title="서비스 목록을 불러오는 중" copy="등록된 서비스를 확인하고 있습니다." />
            </div>
          ) : services.map((service) => (
            <button
              key={service.id}
              type="button"
              onClick={() => openServiceDetail(service)}
              className="service-list-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '1.5rem',
                border: 'none',
                borderBottom: '1px solid var(--border-color)',
                outline: 'none',
                cursor: 'pointer',
                transition: 'background 0.2s',
                background: 'transparent',
                width: '100%',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fbfcfe'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '16px',
                  background: service.status === 'ACTIVE' ? '#eff6ff' : '#f1f5f9',
                  color: service.status === 'ACTIVE' ? 'var(--primary-color)' : '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '1.5rem',
                  flexShrink: 0,
                }}
              >
                <Globe size={24} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-main)' }}>{service.name}</h3>
                  <p
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--text-muted)',
                      maxWidth: '640px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {service.description || '서비스에 대한 설명이 등록되지 않았습니다.'}
                  </p>
                  <span
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: '700',
                      color: 'var(--text-muted)',
                      padding: '2px 8px',
                      background: '#f1f5f9',
                      borderRadius: '6px',
                    }}
                  >
                    {service.code}
                  </span>
                  <span className={`badge badge-${service.status.toLowerCase()}`} style={{ fontSize: '0.625rem' }}>
                    {service.status}
                  </span>
                </div>
              </div>

              <div className="service-list-stats" style={{ display: 'flex', gap: '2rem', marginRight: '3rem', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: '800', color: 'var(--text-main)' }}>{serviceStats[service.code]?.memberCount ?? 0}</div>
                  <div>멤버</div>
                </div>
              </div>

              <div className="btn btn-outline service-list-action" style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', borderRadius: '8px' }}>
                상세 관리 <ChevronRight size={14} />
              </div>
            </button>
          ))}

          {services.length === 0 && !loading && (
            <div style={{ padding: '5rem', textAlign: 'center', color: 'var(--text-muted)' }}>등록된 서비스가 없습니다.</div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ padding: '2.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '800' }}>새 서비스 등록</h2>
              <button onClick={() => setShowCreateModal(false)} className="btn btn-outline" style={{ border: 'none' }}>
                <X />
              </button>
            </div>
            <form onSubmit={handleCreateService} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label className="label" style={{ marginBottom: '0.5rem' }}>서비스 코드 (영문 소문자/숫자)</label>
                <input
                  className="input"
                  value={serviceForm.code}
                  onChange={(e) => setServiceForm({ ...serviceForm, code: e.target.value.toLowerCase() })}
                  required
                  placeholder="예: auth-system"
                />
              </div>
              <div>
                <label className="label" style={{ marginBottom: '0.5rem' }}>서비스 명칭</label>
                <input
                  className="input"
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  required
                  placeholder="예: 통합 인증 시스템"
                />
              </div>
              <div>
                <label className="label" style={{ marginBottom: '0.5rem' }}>서비스 설명</label>
                <textarea
                  className="input"
                  rows={3}
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  placeholder="서비스의 목적과 용도를 입력하세요."
                />
              </div>
              <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowCreateModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 2 }}>
                  서비스 등록 완료
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
